import { Button, PoweredBy } from '@/components';
import { useChatStore } from '@/store';
import { AGENT_AVATAR, CUSTOMER_AVATAR } from '@/utils/constants';
import { FC, memo } from 'react';
import { AtchItems, ButtonAddon, ButtonGroup, ButtonMenu, ButtonSend, InputWrapper, TypoInput } from './ChatActions';
import { Message } from './Message';
import { ChatWrapper, ConversationWrapper } from './styles';
import { useConversation } from './useConversation';

const Conversation: FC = () => {
  const {
    isLoading,
    inputValue,
    fileURL,
    setFileURL,
    scrollerRef,
    handleChange,
    handleFileChange,
    handleKeyPress,
    handleSend,
    handleQuickReply,
    handleGenericTemplate,
  } = useConversation();

  const { messages, lastMsgID } = useChatStore();

  return (
    <ConversationWrapper>
      <ChatWrapper ref={scrollerRef}>
        <Message.Time time={new Date()} />
        <Message.General>
          This conversation will remain available for up to 24 hrs after the chat is closed. To remove it sooner, choose
          End Chat from the drop-down menu.
        </Message.General>

        {messages.map(
          ({
            message: {
              metadata: { id, customer, attending, is_reply },
              text,
              image,
              attachment,
              generic,
              buttons,
              quick_replies,
            },
          }) => {
            const isCustomer = is_reply === 0;
            const avatarURL = isCustomer
              ? customer?.profile_pic || CUSTOMER_AVATAR
              : attending?.profile_pic || AGENT_AVATAR;
            const name = isCustomer ? customer?.name : attending?.name;
            const attachmentURL = attachment?.payload?.url;
            const attachmentType = attachment?.type;
            const isGeneric = generic?.length;
            const isQuickReply = quick_replies?.length;
            const isLastItem = id === lastMsgID;

            return (
              <Message.Wrapper key={id} align={isCustomer ? 'right' : 'left'}>
                <Message.Avatar src={avatarURL} alt={name} style={{ order: isCustomer ? '100' : '-1' }} />
                <Message.Content>
                  {!isCustomer && name && <Message.By by={name} />}

                  {((attachmentURL && attachmentType === 'image') || image) && (
                    <Message.Image src={attachment?.payload.url || image} alt="chat" />
                  )}

                  {!buttons && text && (
                    <Message.Text
                      variant={isCustomer ? 'sender' : 'receiver'}
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  )}

                  {isGeneric && (
                    <Message.Sliders>
                      {generic.map(({ title, subtitle, image_url, buttons }, i) => (
                        <Message.Text key={i} className="generic" variant={isCustomer ? 'sender' : 'receiver'}>
                          {image_url && <Message.Image src={image_url} alt={title || 'Chat'} />}

                          {title && <Message.Heading>{title}</Message.Heading>}

                          {subtitle && <Message.SubHeading dangerouslySetInnerHTML={{ __html: subtitle }} />}

                          {buttons?.map(({ title, ...rest }, j) => (
                            <Message.Button
                              key={j}
                              type="button"
                              variant="white"
                              block
                              onClick={() => handleGenericTemplate({ title, ...rest })}
                            >
                              {title}
                            </Message.Button>
                          ))}
                        </Message.Text>
                      ))}
                    </Message.Sliders>
                  )}

                  {buttons && (
                    <Message.Text className="generic" variant={isCustomer ? 'sender' : 'receiver'}>
                      {(buttons?.text || text) && (
                        <Message.SubHeading dangerouslySetInnerHTML={{ __html: `${buttons?.text || text}` }} />
                      )}

                      {buttons?.buttons?.map(({ title, ...rest }, i) => (
                        <Message.Button
                          key={i}
                          type="button"
                          variant="white"
                          block
                          onClick={() => handleGenericTemplate({ ...rest, title })}
                        >
                          {title}
                        </Message.Button>
                      ))}
                    </Message.Text>
                  )}

                  {isQuickReply && isLastItem && (
                    <Message.ButtonGroup inline>
                      {quick_replies.map(({ title, payload }, i) => (
                        <Button
                          key={i}
                          type="button"
                          rounded="pill"
                          variant="black"
                          onClick={async () => await handleQuickReply(payload, title)}
                        >
                          {title}
                        </Button>
                      ))}
                    </Message.ButtonGroup>
                  )}
                </Message.Content>
              </Message.Wrapper>
            );
          },
        )}
      </ChatWrapper>

      <InputWrapper className={fileURL ? 'active file' : inputValue ? 'active' : ''}>
        <TypoInput value={inputValue} onChange={handleChange} onKeyPress={handleKeyPress} />
        <AtchItems isLoading={isLoading === 'FILE'} atchPreviewURL={fileURL} onDelete={() => setFileURL('')} />
        <ButtonGroup>
          <ButtonAddon onChange={handleFileChange} />
          {isLoading === 'NONE' && (inputValue || fileURL) ? (
            <ButtonSend onClick={() => handleSend()} />
          ) : (
            <ButtonMenu
              onMenuItemClick={(item) => {
                const { payload, title } = item;
                handleQuickReply(payload, title, 'postback');
              }}
            />
          )}
        </ButtonGroup>
      </InputWrapper>
      <PoweredBy style={{ margin: '8px 16px', justifyContent: 'end' }} />
    </ConversationWrapper>
  );
};

export default memo(Conversation);
